import React, { useState, useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Dashboard from './pages/Dashboard';
import Personnel from './pages/Personnel';
import Employees from './pages/Employees';
import Organization from './pages/Organization';
import Devices from './pages/Devices';
import Shifts from './pages/Shifts';
import Payroll from './pages/Payroll';
import Reports from './pages/Reports';
import DailyTimesheet from './pages/DailyTimesheet';
import AttendanceLogs from './pages/AttendanceLogs';
import WorkGroups from './pages/WorkGroups';
import Import from './pages/Import';
import Leaves from './pages/Leaves';
import Settings from './pages/Settings';
import Holidays from './pages/Holidays';
import Profile from './pages/Profile';
import Locations from './pages/Locations';
import Positions from './pages/Positions';
import Users from './pages/Users';
import Permissions from './pages/Permissions';
import Login from './pages/Login';
import AttendanceImport from './pages/AttendanceImport';
import EmployeeStats from './pages/EmployeeStats';
import NFCAttendance from './pages/NFCAttendance';
import FaceAttendance from './pages/FaceAttendance';
import License from './pages/License';
import MealDashboard from './pages/MealDashboard';
import MealPeriods from './pages/MealPeriods';
import MealDevices from './pages/MealDevices';
import MealEntitlements from './pages/MealEntitlements';
import MealMenus from './pages/MealMenus';
import MealTransactions from './pages/MealTransactions';
import MealCatering from './pages/MealCatering';
import MealReports from './pages/MealReports';
import MealPersonnel from './pages/MealPersonnel';
import Sites from './pages/Sites';
import MobileInvite from './pages/MobileInvite';
import LeaveTypes from './pages/LeaveTypes';
// 2026-08-13: Teshis paneli — MENUDE YOK, sadece /#/system-errors adresiyle acilir
import SystemErrors from './pages/SystemErrors';
import MobileLogs from './pages/MobileLogs';
import MobileSettings from './pages/MobileSettings';
import { AppProvider, useAppContext } from './context/AppContext';
import { PermissionProvider } from './context/PermissionContext';
import authService from './services/authService';
import licenseService from './services/licenseService';
import { Role } from './types';
// 2026-08-15: Tarayicinin standart alert/confirm kutusu yerine uygulama ici modal
import GlobalModal from './components/GlobalModal';

// Protected Route komponenti
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const checkAuth = () => {
      const authenticated = authService.isAuthenticated();
      setIsAuthenticated(authenticated);
      setIsChecking(false);

      if (!authenticated) {
        navigate('/login', { state: { from: location }, replace: true });
      }
    };

    checkAuth();
  }, [navigate, location]);

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : null;
};

// Departman Yoneticisi rota korumasi
const DeptManagerRouteGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAppContext();
  const location = useLocation();

  // NFC Operator - sadece NFC sayfasina erisim
  if (currentUser.role === Role.NFC_OPERATOR) {
    return <Navigate to="/nfc-attendance" replace />;
  }

  if (currentUser.role === Role.DEPARTMENT_MANAGER) {
    const allowedPaths = ['/timesheet', '/reports', '/profile'];
    const isAllowed = allowedPaths.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));
    if (!isAllowed) {
      return <Navigate to="/timesheet" replace />;
    }
  }

  return <>{children}</>;
};

// Ana Layout
const MainLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="flex min-h-screen bg-slate-50 font-sans">
      <Sidebar />
      <main className="flex-1 ml-64 flex flex-col h-screen overflow-hidden">
        <Header />
        <div className="flex-1 overflow-y-auto p-8">
          <div className="max-w-7xl mx-auto pb-10">
            {children}
          </div>
        </div>
      </main>
    </div>
  );
};

// App Routes
const AppRoutes: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => authService.isAuthenticated());
  const [licenseActive, setLicenseActive] = useState<boolean | null>(null);
  const [licenseChecked, setLicenseChecked] = useState(false);

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    authService.logout();
    setIsAuthenticated(false);
  };

  // Lisans kontrolu — licenseService.getStatus() zaten normalize edilmis
  // { active: boolean, ... } dondurur. Ge$ici bypass kaldirildi (2026-08-08).
  useEffect(() => {
    if (isAuthenticated) {
      licenseService.getStatus()
        .then((status) => {
          setLicenseActive(status.active === true);
          setLicenseChecked(true);
        })
        .catch(() => {
          setLicenseActive(false);
          setLicenseChecked(true);
        });
    }
  }, [isAuthenticated]);

  // Login sayfasi icin ozel kontrol
  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login onLogin={handleLogin} />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Lisans kontrolu bekleniyor
  if (!licenseChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Lisans aktif degil - sadece lisans sayfasina erisim
  if (!licenseActive) {
    return (
      <Routes>
        <Route path="/login" element={<Navigate to="/license" replace />} />
        <Route path="/license" element={
          <ProtectedRoute>
            <MainLayout>
              <License />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="/license" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      {/* NFC sayfasi - tam ekran, sidebar'siz */}
      <Route path="/nfc-attendance" element={
        <ProtectedRoute>
          <NFCAttendance />
        </ProtectedRoute>
      } />
      {/* Yuz Tanima sayfasi - tam ekran, sidebar'siz */}
      <Route path="/face-attendance" element={
        <ProtectedRoute>
          <FaceAttendance />
        </ProtectedRoute>
      } />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DeptManagerRouteGuard>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/employees" element={<Employees />} />
                <Route path="/personnel" element={<Personnel />} />
                <Route path="/permissions" element={<Permissions />} />
                <Route path="/organization" element={<Organization />} />
                <Route path="/locations" element={<Locations />} />
                <Route path="/positions" element={<Positions />} />
                <Route path="/devices" element={<Devices />} />
                <Route path="/shifts" element={<Shifts />} />
                <Route path="/payroll" element={<Payroll />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/attendance-logs" element={<AttendanceLogs />} />
                <Route path="/timesheet" element={<DailyTimesheet />} />
                <Route path="/workgroups" element={<WorkGroups />} />
                <Route path="/import" element={<Import />} />
                <Route path="/attendance-import" element={<AttendanceImport />} />
                <Route path="/leaves" element={<Leaves />} />
                <Route path="/holidays" element={<Holidays />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/users" element={<Users />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/license" element={<License />} />
                <Route path="/employee-stats" element={<EmployeeStats />} />
                <Route path="/meal-dashboard" element={<MealDashboard />} />
                <Route path="/meal-periods" element={<MealPeriods />} />
                <Route path="/meal-devices" element={<MealDevices />} />
                <Route path="/meal-entitlements" element={<MealEntitlements />} />
                <Route path="/meal-menus" element={<MealMenus />} />
                <Route path="/meal-transactions" element={<MealTransactions />} />
                <Route path="/meal-catering" element={<MealCatering />} />
                <Route path="/meal-reports" element={<MealReports />} />
                <Route path="/meal-personnel" element={<MealPersonnel />} />
                <Route path="/sites" element={<Sites />} />
                <Route path="/leave-types" element={<LeaveTypes />} />
                {/* Teshis paneli — menude gorunmez, elle URL yazilarak erisilir */}
                <Route path="/system-errors" element={<SystemErrors />} />
                <Route path="/mobile-invite" element={<MobileInvite />} />
                <Route path="/mobile-logs" element={<MobileLogs />} />
                <Route path="/mobile-settings" element={<MobileSettings />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </DeptManagerRouteGuard>
            </MainLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

const App: React.FC = () => {
  // Uygulama basladiginda auth durumunu baslat (token'i header'a ekle)
  useEffect(() => {
    authService.initializeAuth();
  }, []);

  return (
    <PermissionProvider>
      <AppProvider>
        <HashRouter>
          <AppRoutes />
        </HashRouter>
      </AppProvider>
      <GlobalModal />
    </PermissionProvider>
  );
};

export default App;
